// BeaconClaimDisplay.jsx  →  src/components/Display/BeaconClaimDisplay.jsx
// MagicBeacon order page for the DISPLAY (1280x800, touch).
// Opened from "Claim My MagicBeacon" on BeaconPromoDisplay.
//
// Server (routes/integrations.js):
//   GET  /integrations/beacon-order/offer?device_id=   prices + existing order
//   POST /integrations/beacon-order/address            billing address (after phone check)
//   POST /integrations/beacon-order                    place the order (status: pending)
//   GET  /integrations/beacon-order?device_id=         order status for polling
//
// Prices always come from the server. The add-on prices below are display
// text only (the add-on is NOT charged here).
//
// TV: same page with a remote layer — lime focus ring, Up/Down (and Left/Right
// where there's a row) move, OK presses, Back backs out. The keypad also takes
// the remote's number keys; the address form uses the TV's on-screen keyboard.

import React, { useEffect, useRef, useState } from "react";
import axios from "../../config/axios";
import useGlobalStore from "../../store/global.store";
import { useTV } from "../../hooks/useTV";

const ASSET_BASE = "/beacon-promo/";
const ADDON_TEXT = { monthly: "$4.99/mo", yearly: "$55/yr", six_month: "$30 every 6 months" };
const STAGE_W = 1280;
const STAGE_H = 800;
const POLL_MS = 4000;
const POLL_GIVE_UP_MS = 3 * 60 * 1000;
const LIME = "#3ADF3C";

// ── Edit here ───────────────────────────────────────────────────────────────
const SHIP_TEXT = "Ships within 1–2 business days";
const SUPPORTED_SHOWS = [
  "Happily Ever After",
  "Luminous",
  "Fantasmic!",
  "Mickey's Not-So-Spooky Spectacular (MK)",
  "Minnie's Wonderful Christmastime Fireworks (MK)",
  "Wondrous (DL)",
];
const SHOWS_NOTE = "New shows are added as they premiere, including seasonal and holiday shows.";
// ────────────────────────────────────────────────────────────────────────────

const C = {
  bg: "#D6DBE5", card: "#F3F5F9", ink: "#0E1018", sub: "#0E1018", muted: "#3F4558", line: "#C3C9D6",
  blue: "#007BFF", pink: "#E0249E", red: "#C62828",
};
const RAINBOW = ["#FF5C93", "#FF8A3D", "#FFD23D", "#B6E04A", "#3EE08A", "#3CC9FF", "#6FB0FF", "#8F9BFF", "#B48CFF", "#E07CFF", "#FF6FC8"];

const FEATURES = [
  { key: "live", title: "Live Show Sync", text: "Bands sync to live fireworks in VIP Magical MultiView and VIP Door 2 close-up fireworks", vip: true, icon: "M12 2l2.4 6.9H22l-6 4.4 2.3 7L12 16l-6.3 4.3 2.3-7-6-4.4h7.6z" },
  { key: "tink", title: "Tink Alert", text: "When Tink flies over the castle each night", vip: false, icon: "M12 3c-1 4-4 6-8 7 4 1 7 3 8 7 1-4 4-6 8-7-4-1-7-3-8-7z" },
  { key: "color", title: "Colors & Designs", text: "Choose any color or make your own light designs", vip: false, icon: "M12 3a9 9 0 100 18c1 0 1.6-.8 1.6-1.6 0-.5-.2-.8-.5-1.1-.3-.3-.5-.7-.5-1.1 0-.9.7-1.6 1.6-1.6H16a5 5 0 005-5C21 6.6 17 3 12 3z" },
  { key: "alerts", title: "Timers & Alerts", text: "Make your MagicBands part of your daily routine", vip: false, icon: "M12 22a2 2 0 002-2h-4a2 2 0 002 2zm6-6V11a6 6 0 00-5-5.9V4a1 1 0 00-2 0v1.1A6 6 0 006 11v5l-2 2v1h16v-1z" },
];

const CSS = `
.mbc-root{position:fixed;inset:0;z-index:9999;background:${C.bg};overflow:hidden;font-family:'Lato','Segoe UI',system-ui,sans-serif;-webkit-tap-highlight-color:transparent}
.mbc-stage{position:absolute;left:50%;top:50%;width:${STAGE_W}px;height:${STAGE_H}px;transform-origin:50% 50%;overflow:hidden;color:${C.ink}}
.mbc-stage *{box-sizing:border-box}
.mbc-btn{font-family:inherit;cursor:pointer;border:0;padding:0;margin:0;background:none;color:inherit;-webkit-appearance:none;appearance:none;touch-action:manipulation;transition:transform .12s ease}
.mbc-btn:active{transform:scale(.97)}
.mbc-btn:disabled{cursor:default}
.mbc-rainbow{background-image:linear-gradient(90deg,#D61F8A,#E8541E,#D99A00,#23A046,#1585D8,#5452E0,#9B2FD0);background-size:100% 100%;-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-fill-color:transparent}
.mbc-in{animation:mbcIn .3s ease both}
@keyframes mbcIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
.mbc-input{width:100%;height:52px;border-radius:12px;border:2px solid ${C.line};padding:0 14px;font-size:20px;font-family:inherit;color:${C.ink};background:#fff;outline:none}
.mbc-input:focus{border-color:${C.blue}}
.mbc-spin{width:46px;height:46px;border-radius:999px;border:5px solid #E3E5EE;border-top-color:${C.blue};animation:mbcSpin 1s linear infinite;margin:0 auto}
@keyframes mbcSpin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion: reduce){.mbc-in{animation:none}}
`;

// Fits the 1280x800 stage to the screen. While a text box is focused, the
// on-screen keyboard shrinks the window — ignore those resizes so the page
// keeps its full-screen size behind the keyboard instead of shrinking.
function useFitScale() {
  const typing = () => {
    const a = document.activeElement;
    return !!a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA");
  };
  const measure = () => ({ w: window.innerWidth, h: window.innerHeight });
  const [view, setView] = useState(measure);
  useEffect(() => {
    const onResize = () => { if (!typing()) setView(measure()); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return { scale: Math.min(view.w / STAGE_W, view.h / STAGE_H), viewH: view.h };
}

const money = (cents) => "$" + (cents % 100 === 0 ? cents / 100 : (cents / 100).toFixed(2));
const errMsg = (err, fallback) => err?.response?.data?.message || fallback;

function Icon({ d }) {
  return <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path d={d} fill={C.blue} /></svg>;
}

function Sheet({ children, top }) {
  return (
    <div style={{ position: "absolute", inset: 0, background: "rgba(21,24,38,.55)", display: "flex", alignItems: top ? "flex-start" : "center", justifyContent: "center", paddingTop: top ? 24 : 0 }}>
      <div className="mbc-in" style={{ width: 600, background: C.card, borderRadius: 24, padding: "28px 34px", textAlign: "center" }}>
        {children}
      </div>
    </div>
  );
}

const PrimaryBtn = ({ children, onClick, disabled, style }) => (
  <button type="button" className="mbc-btn" onClick={onClick} disabled={disabled}
    style={{ flex: 1, height: 60, borderRadius: 14, background: C.blue, color: "#fff", fontSize: 20, fontWeight: 900, opacity: disabled ? 0.45 : 1, ...style }}>
    {children}
  </button>
);
const GrayBtn = ({ children, onClick, style }) => (
  <button type="button" className="mbc-btn" onClick={onClick}
    style={{ flex: "0 0 160px", height: 60, borderRadius: 14, background: "#EEF0F6", fontSize: 18, fontWeight: 700, ...style }}>
    {children}
  </button>
);

export default function BeaconClaimDisplay({ onBack, assetBase = ASSET_BASE }) {
  const device = useGlobalStore((state) => state.device);
  const hasVip = useGlobalStore((state) => state.hasVipAddOn);
  const { isTv } = useTV();
  const { scale, viewH } = useFitScale();
  const rootRef = useRef(null);
  const stageRef = useRef(null);

  const [offer, setOffer] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [agreed, setAgreed] = useState(false);

  // Order flow: null | phone | address | custom | placing | processing | done | failed
  const [step, setStep] = useState(null);
  const [showShows, setShowShows] = useState(false);
  const [digits, setDigits] = useState("");
  const [busy, setBusy] = useState(false);
  const [stepError, setStepError] = useState("");
  const [address, setAddress] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", line1: "", line2: "", city: "", state: "", zip: "" });
  const [resultMsg, setResultMsg] = useState("");
  const pollRef = useRef(null);
  const [focusId, setFocusId] = useState(null);
  const fieldRefs = {
    first: useRef(null), last: useRef(null), line1: useRef(null), line2: useRef(null),
    city: useRef(null), state: useRef(null), zip: useRef(null),
  };

  const deviceName = isTv ? "Wait Time TV" : "Wait Time Display";
  const addon = ADDON_TEXT[offer?.billing_period] || ADDON_TEXT.monthly;

  // Load the offer (prices come from the server).
  useEffect(() => {
    if (!device?._id) return;
    axios
      .get("/integrations/beacon-order/offer", { params: { device_id: device._id } })
      .then((res) => setOffer(res.data))
      .catch(() => setLoadError("Couldn't load MagicBeacon pricing. Please try again in a moment."));
  }, [device?._id]);

  useEffect(() => () => clearInterval(pollRef.current), []);

    // The WebView scrolls the page to show a focused text box, even inside
  // overflow:hidden. Put everything back when the box loses focus or the
  // keyboard opens/closes, so nothing stays stuck at the top.
  useEffect(() => {
    const reset = () => {
      [rootRef.current, stageRef.current, document.scrollingElement, document.body].forEach((el) => {
        if (el) el.scrollTop = 0;
      });
      window.scrollTo(0, 0);
    };
    const onFocusOut = () => setTimeout(reset, 50);
    document.addEventListener("focusout", onFocusOut);
    window.addEventListener("resize", reset);
    return () => {
      document.removeEventListener("focusout", onFocusOut);
      window.removeEventListener("resize", reset);
    };
  }, []);

  const resetFlow = () => {
    clearInterval(pollRef.current);
    setStep(null); setDigits(""); setBusy(false); setStepError(""); setAddress(null);
  };

  // Step 1: phone digits → fetch billing address
  const pressKey = (k) => {
    if (busy) return;
    setStepError("");
    if (k === "del") setDigits((d) => d.slice(0, -1));
    else if (k === "clear") setDigits("");
    else setDigits((d) => (d.length < 4 ? d + k : d));
  };
  const submitPhone = async () => {
    if (digits.length !== 4) return;
    setBusy(true); setStepError("");
    try {
      const res = await axios.post("/integrations/beacon-order/address", {
        device_id: device._id,
        phone_last_4_digits: digits,
      });
      setAddress(res.data?.address || null);
      setStep(res.data?.address ? "address" : "custom");
    } catch (err) {
      setStepError(errMsg(err, "That didn't work. Check the digits and try again."));
    } finally {
      setBusy(false);
    }
  };

  // Step 3: place the order, then poll until it's charged or declined
  const pollStatus = () => {
    const started = Date.now();
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get("/integrations/beacon-order", { params: { device_id: device._id } });
        const s = res.data?.status;
        if (s === "paid" || s === "complete") {
          clearInterval(pollRef.current);
          setStep("done");
        } else if (s === "failed") {
          clearInterval(pollRef.current);
          setResultMsg(res.data?.error_message || "Your order couldn't be completed.");
          setStep("failed");
        }
      } catch (e) { /* keep trying */ }
      if (Date.now() - started > POLL_GIVE_UP_MS) {
        clearInterval(pollRef.current);
        setResultMsg("Your order is still processing. We'll text you as soon as it's confirmed.");
        setStep("done");
      }
    }, POLL_MS);
  };

  const placeOrder = async (source) => {
    setBusy(true); setStepError("");
    const body = { device_id: device._id, phone_last_4_digits: digits, ship_to_source: source };
    if (source === "custom") body.ship_to = form;
    setStep("placing");
    try {
      await axios.post("/integrations/beacon-order", body);
      setResultMsg("");
      setStep("processing");
      pollStatus();
    } catch (err) {
      setResultMsg(errMsg(err, "We couldn't place your order. Please try again."));
      setStep("failed");
    } finally {
      setBusy(false);
    }
  };

  const formOk = form.first_name && form.last_name && form.line1 && form.city && form.state.trim().length === 2 && form.zip;
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "state" ? e.target.value.toUpperCase().slice(0, 2) : e.target.value }));

  const existing = offer?.existing_order;
  const total = offer ? money(offer.total_cents) : "";

  // ===== TV remote layer =====
  const FIELD_KEYS = { first: "first_name", last: "last_name", line1: "line1", line2: "line2", city: "city", state: "state", zip: "zip" };
  const screenKey = showShows ? "shows" : step || "main";
  const layouts = {
    main: existing ? [["back"], ["shows"]] : [["back"], ["shows"], ["agree"], ["order"]],
    shows: [["gotit"]],
    phone: [["1", "2", "3"], ["4", "5", "6"], ["7", "8", "9"], ["clear", "0", "del"], ["pcancel", "pcontinue"]],
    address: [["elsewhere", "yes"], ["acancel"]],
    custom: [["first", "last"], ["line1"], ["line2"], ["city", "state", "zip"], ["cback", "place"]],
    done: [["done"]],
    failed: [["fclose", "retry"]],
    placing: [],
    processing: [],
  };
  const layout = layouts[screenKey] || [];
  const firstFocus = {
    main: existing ? "back" : agreed ? "order" : "agree",
    shows: "gotit", phone: "1", address: "yes", custom: "first", done: "done", failed: "retry",
  };
  const tvActions = {
    back: onBack,
    shows: () => setShowShows(true),
    agree: () => { setAgreed((a) => { if (!a) setFocusId("order"); return !a; }); },
    order: () => { if (agreed && offer && offer.enabled) { setStep("phone"); setStepError(""); } },
    gotit: () => setShowShows(false),
    pcancel: resetFlow,
    pcontinue: submitPhone,
    elsewhere: () => setStep("custom"),
    yes: () => placeOrder("billing"),
    acancel: resetFlow,
    cback: address ? () => setStep("address") : resetFlow,
    place: () => { if (formOk && !busy) placeOrder("custom"); },
    done: onBack,
    fclose: onBack,
    retry: () => { setDigits(""); setStepError(""); setStep("phone"); },
  };
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "clear", "del"].forEach((k) => { tvActions[k] = () => pressKey(k); });
  const backFor = {
    main: onBack, shows: () => setShowShows(false), phone: resetFlow, address: resetFlow,
    custom: tvActions.cback, done: onBack, failed: onBack,
  };

  useEffect(() => {
    if (isTv) setFocusId(firstFocus[screenKey] || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTv, screenKey]);

  // Text fields need real focus so the TV keyboard opens; buttons don't.
  useEffect(() => {
    if (!isTv) return;
    const r = fieldRefs[focusId];
    if (r && r.current) r.current.focus();
    else if (document.activeElement && document.activeElement.tagName === "INPUT") document.activeElement.blur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTv, focusId]);

  const navRef = useRef({});
  navRef.current = { layout, tvActions, backFor, focusId, screenKey };
  useEffect(() => {
    if (!isTv) return;
    const onKey = (e) => {
      const { layout: lay, tvActions: act, backFor: bk, focusId: fid, screenKey: sk } = navRef.current;
      if (sk === "placing" || sk === "processing") return;
      const k = e.key;
      const isBack = k === "Escape" || k === "GoBack" || k === "BrowserBack" || e.keyCode === 461 || e.keyCode === 10009;
      const stop = () => { e.preventDefault(); e.stopPropagation(); };
      const inField = !!FIELD_KEYS[fid];
      const t = e.target;
      const move = (dr, dc) => {
        let r = lay.findIndex((row) => row.includes(fid));
        if (r < 0) { if (lay[0]) setFocusId(lay[0][0]); return; }
        let c = lay[r].indexOf(fid);
        if (dc) c = Math.max(0, Math.min(lay[r].length - 1, c + dc));
        if (dr) {
          const nr = Math.max(0, Math.min(lay.length - 1, r + dr));
          if (nr !== r) {
            const from = lay[r].length - 1 || 1;
            c = Math.round((c / from) * (lay[nr].length - 1));
            r = nr;
          }
        }
        setFocusId(lay[r][c]);
      };
      if (k === "ArrowUp" || k === "ArrowDown") { stop(); move(k === "ArrowDown" ? 1 : -1, 0); return; }
      if (k === "ArrowLeft" || k === "ArrowRight") {
        // Inside a text field, arrows move the cursor until it hits an edge.
        if (inField && t && t.tagName === "INPUT") {
          const atStart = t.selectionStart === 0 && t.selectionEnd === 0;
          const atEnd = t.selectionStart === t.value.length;
          if ((k === "ArrowLeft" && !atStart) || (k === "ArrowRight" && !atEnd)) return;
        }
        stop(); move(0, k === "ArrowRight" ? 1 : -1); return;
      }
      if (k === "Enter") {
        stop();
        if (inField) {
          // Enter in a field → next field (then Place Order).
          const order = ["first", "last", "line1", "line2", "city", "state", "zip", "place"];
          setFocusId(order[order.indexOf(fid) + 1] || "place");
          return;
        }
        if (act[fid]) act[fid]();
        return;
      }
      if (k === " " && !inField) { stop(); if (act[fid]) act[fid](); return; }
      if (sk === "phone" && /^[0-9]$/.test(k)) { stop(); act[k](); return; }
      if (isBack) { stop(); if (bk[sk]) bk[sk](); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTv]);

  const ring = (id) => (isTv && focusId === id ? { boxShadow: `0 0 0 4px ${LIME}` } : {});
  const fieldRing = (id) => (isTv && focusId === id ? { borderColor: LIME, boxShadow: `0 0 0 2px ${LIME}` } : {});

  return (
        <div ref={rootRef} className="mbc-root" role="dialog" aria-modal="true" aria-label="Order MagicBeacon" style={{ bottom: "auto", height: viewH }}>
      <style>{CSS}</style>
      <div ref={stageRef} className="mbc-stage" style={{ transform: `translate(-50%, -50%) scale(${scale})` }}>
        {/* Back */}
        <button type="button" className="mbc-btn" onClick={onBack}
          style={{ ...ring("back"), position: "absolute", left: 32, top: 26, height: 48, padding: "0 20px 0 14px", borderRadius: 999, background: C.card, border: `1px solid ${C.line}`, fontSize: 17, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
          Back
        </button>

        {/* Product photo (static image — nothing to decode) */}
        <div style={{ position: "absolute", left: 56, top: 96, width: 500, height: 500, borderRadius: 28, background: C.card, border: `1px solid ${C.line}`, overflow: "hidden" }}>
          <img src={assetBase + "magicbeacon-product.jpg"} alt="The MagicBeacon" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
        <div style={{ position: "absolute", left: 56, top: 612, width: 500, textAlign: "center", fontSize: 21, fontWeight: 900, color: C.ink, lineHeight: 1.4 }}>
          Plugs into the USB port on your {deviceName}.
        </div>

        {/* Details */}
        <div style={{ position: "absolute", left: 612, top: 40, width: 612 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.ink }}>Wait Time Device Accessory</div>
          <div style={{ fontSize: 52, fontWeight: 900, letterSpacing: -1, lineHeight: 1.05, marginTop: 2 }}><span className="mbc-rainbow">MagicBeacon</span></div>

          {offer ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 14 }}>
                <span style={{ fontSize: 46, fontWeight: 900, letterSpacing: -1 }}>{money(offer.price_cents)}</span>
                {offer.price_cents < offer.regular_cents && (
                  <span style={{ fontSize: 24, color: C.muted, textDecoration: "line-through" }}>{money(offer.regular_cents)}</span>
                )}
                {offer.offer_label && (
                  <span style={{ borderLeft: `4px solid ${C.pink}`, paddingLeft: 10, color: C.pink, fontSize: 18, fontWeight: 900, lineHeight: 1.15 }}>{offer.offer_label}</span>
                )}
              </div>
              <div style={{ fontSize: 15, color: C.muted, marginTop: 2 }}>{offer.shipping_cents > 0 ? `+ ${money(offer.shipping_cents)} shipping` : <b style={{ color: C.ink }}>FREE shipping!</b>} · {offer.ship_text || SHIP_TEXT}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.ink, marginTop: 4 }}>
                *Subscription add-on required (+{addon})
              </div>
            </>
          ) : (
            <div style={{ fontSize: 18, color: loadError ? C.red : C.sub, marginTop: 22, minHeight: 74 }}>{loadError || "Loading price…"}</div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginTop: 12 }}>
            {FEATURES.map((f) => (
              <div key={f.key} style={{ position: "relative", display: "flex", gap: 12, padding: "12px 14px", borderRadius: 14, background: C.card, border: `1px solid ${C.line}` }}>
                <div style={{ width: 38, height: 38, flex: "0 0 38px", borderRadius: 999, background: "#EAF2FF", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon d={f.icon} /></div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 900, display: "flex", alignItems: "center", gap: 8 }}>
                    {f.title}
                                      </div>
                  <div style={{ fontSize: 15.5, color: C.muted, lineHeight: 1.35, marginTop: 2 }}>{f.text}</div>
                  {f.key === "live" && (
                    <button type="button" className="mbc-btn" onClick={() => setShowShows(true)}
                      style={{ ...ring("shows"), borderRadius: 6, padding: "0 4px", marginLeft: -4, display: "block", marginTop: 6, fontSize: 18, fontWeight: 800, color: C.blue, textDecoration: "underline" }}>
                      See supported shows ›
                    </button>
                  )}
                  {f.vip && !hasVip && (
                    <span style={{ display: "inline-block", marginTop: 7, padding: "3px 10px", borderRadius: 999, background: "#7C3AED", color: "#fff", fontSize: 12.5, fontWeight: 900, whiteSpace: "nowrap" }}>VIP Package Required</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", flexWrap: "nowrap", gap: 14, marginTop: 12 }}>
            {["Works with MagicBand+ only", "One Beacon per home", "Wi\u2011Fi required"].map((t) => (
              <span key={t} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 16.5, fontWeight: 800, color: C.ink, whiteSpace: "nowrap" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                {t}
              </span>
            ))}
          </div>

          {existing ? (
            <div style={{ marginTop: 18, padding: "16px 18px", borderRadius: 16, background: "#E8F7EE", color: "#14532D", fontSize: 18, fontWeight: 700, lineHeight: 1.4 }}>
              Your MagicBeacon is already on order for your home. Only one Beacon is needed per household, even if you have multiple Wait Time devices. Note: each device that controls the Beacon needs its own MagicBeacon add-on subscription.
            </div>
          ) : (
            <>
              <button type="button" className="mbc-btn" onClick={() => setAgreed((a) => !a)} aria-pressed={agreed}
                style={{ ...ring("agree"), borderRadius: 10, padding: isTv ? 6 : 0, display: "flex", alignItems: "flex-start", gap: 12, marginTop: 12, textAlign: "left", width: "100%" }}>
                <span style={{ width: 26, height: 26, flex: "0 0 26px", borderRadius: 7, border: `2px solid ${agreed ? C.blue : "#B8BCCB"}`, background: agreed ? C.blue : C.card, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
                  {agreed && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>}
                </span>
                <span style={{ fontSize: 17, lineHeight: 1.4, color: C.ink }}>
                  I understand that a +{addon} add-on to my current subscription is required for the MagicBeacon to operate. The subscription change happens when I receive &amp; activate my Beacon.
                </span>
              </button>
              <PrimaryBtn
                onClick={() => { setStep("phone"); setStepError(""); }}
                disabled={!agreed || !offer || !offer.enabled}
                style={{ ...ring("order"), width: "100%", flex: "none", height: 58, borderRadius: 16, marginTop: 14, fontSize: 22 }}>
                {offer && !offer.enabled ? "Orders paused — check back soon" : `Order Now · ${total}${offer && offer.shipping_cents > 0 ? " incl. shipping" : ""}`}
              </PrimaryBtn>
              <div style={{ fontSize: 14, color: C.ink, marginTop: 8, textAlign: "center" }}>
                Charged to the card on your WaitSigns account. No long-term commitment.
              </div>
            </>
          )}
        </div>

        {/* ===== Order flow sheets ===== */}
        {step === "phone" && (
          <Sheet>
            <div style={{ fontSize: 26, fontWeight: 900 }}>Confirm it's you</div>
            <p style={{ fontSize: 17, color: C.sub, margin: "8px 0 18px" }}>Enter the last 4 digits of the phone number on your account.</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 18 }}>
              {[0, 1, 2, 3].map((n) => (
                <div key={n} style={{ width: 58, height: 66, borderRadius: 12, border: `2px solid ${digits.length === n ? C.blue : C.line}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, fontWeight: 900 }}>{digits[n] || ""}</div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "del"].map((k, i) => (
                <button key={i} type="button" className="mbc-btn" onClick={() => pressKey(k)} aria-label={k === "del" ? "Delete" : k}
                  style={{ ...ring(k), height: 50, borderRadius: 12, background: "#EEF0F6", fontSize: k === "clear" ? 16 : 22, fontWeight: 700 }}>{k === "del" ? "⌫" : k === "clear" ? "CLEAR" : k}</button>
              ))}
            </div>
            {stepError && <div style={{ color: C.red, fontSize: 16, fontWeight: 700, marginBottom: 12 }}>{stepError}</div>}
            <div style={{ display: "flex", gap: 12 }}>
              <GrayBtn onClick={resetFlow} style={ring("pcancel")}>Cancel</GrayBtn>
              <PrimaryBtn onClick={submitPhone} disabled={digits.length !== 4 || busy} style={ring("pcontinue")}>{busy ? "Checking…" : "Continue"}</PrimaryBtn>
            </div>
          </Sheet>
        )}

        {step === "address" && address && (
          <Sheet>
            <div style={{ fontSize: 26, fontWeight: 900 }}>Ship to this address?</div>
            <div style={{ margin: "18px 0 22px", padding: "16px 18px", borderRadius: 14, background: "#F5F6FA", fontSize: 20, lineHeight: 1.45, textAlign: "left" }}>
              <b>{address.first_name} {address.last_name}</b><br />
              {address.line1}{address.line2 ? `, ${address.line2}` : ""}<br />
              {address.city}, {address.state} {address.zip}
            </div>
            <div style={{ fontSize: 16, color: C.sub, marginBottom: 16 }}>Total today: <b style={{ color: C.ink }}>{total}</b> charged to the card on your account.</div>
            <div style={{ display: "flex", gap: 12 }}>
              <GrayBtn onClick={() => setStep("custom")} style={{ flex: "0 0 220px", ...ring("elsewhere") }}>No, ship elsewhere</GrayBtn>
              <PrimaryBtn onClick={() => placeOrder("billing")} disabled={busy} style={ring("yes")}>Yes · Place Order</PrimaryBtn>
            </div>
            <button type="button" className="mbc-btn" onClick={resetFlow} style={{ ...ring("acancel"), borderRadius: 8, padding: "2px 8px", marginTop: 14, fontSize: 16, color: C.sub, textDecoration: "underline" }}>Cancel</button>
          </Sheet>
        )}

        {step === "custom" && (
          <Sheet top>
            <div style={{ fontSize: 24, fontWeight: 900 }}>Where should we ship it?</div>
            {!address && <p style={{ fontSize: 15, color: C.sub, margin: "6px 0 0" }}>We couldn't find a full address on your account.</p>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, margin: "16px 0 12px", textAlign: "left" }}>
              <input ref={fieldRefs.first} onFocus={() => isTv && setFocusId("first")} className="mbc-input" placeholder="First name" value={form.first_name} onChange={setField("first_name")} autoComplete="off" style={fieldRing("first")} />
              <input ref={fieldRefs.last} onFocus={() => isTv && setFocusId("last")} className="mbc-input" placeholder="Last name" value={form.last_name} onChange={setField("last_name")} autoComplete="off" style={fieldRing("last")} />
              <input ref={fieldRefs.line1} onFocus={() => isTv && setFocusId("line1")} className="mbc-input" placeholder="Street address" value={form.line1} onChange={setField("line1")} autoComplete="off" style={{ gridColumn: "1 / span 2", ...fieldRing("line1") }} />
              <input ref={fieldRefs.line2} onFocus={() => isTv && setFocusId("line2")} className="mbc-input" placeholder="Apt / Suite (optional)" value={form.line2} onChange={setField("line2")} autoComplete="off" style={{ gridColumn: "1 / span 2", ...fieldRing("line2") }} />
              <input ref={fieldRefs.city} onFocus={() => isTv && setFocusId("city")} className="mbc-input" placeholder="City" value={form.city} onChange={setField("city")} autoComplete="off" style={fieldRing("city")} />
              <div style={{ display: "flex", gap: 10 }}>
                <input ref={fieldRefs.state} onFocus={() => isTv && setFocusId("state")} className="mbc-input" placeholder="State" value={form.state} onChange={setField("state")} autoComplete="off" style={{ width: 110, ...fieldRing("state") }} />
                <input ref={fieldRefs.zip} onFocus={() => isTv && setFocusId("zip")} className="mbc-input" placeholder="ZIP" value={form.zip} onChange={setField("zip")} inputMode="numeric" autoComplete="off" style={fieldRing("zip")} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <GrayBtn onClick={address ? () => setStep("address") : resetFlow} style={ring("cback")}>{address ? "Back" : "Cancel"}</GrayBtn>
              <PrimaryBtn onClick={() => placeOrder("custom")} disabled={!formOk || busy} style={ring("place")}>Place Order · {total}</PrimaryBtn>
            </div>
          </Sheet>
        )}

        {(step === "placing" || step === "processing") && (
          <Sheet>
            <div className="mbc-spin" />
            <div style={{ fontSize: 24, fontWeight: 900, marginTop: 18 }}>Placing your order…</div>
            <p style={{ fontSize: 17, color: C.sub, margin: "8px 0 0" }}>This usually takes about a minute. Please don't close this screen.</p>
          </Sheet>
        )}

        {showShows && (
          <Sheet>
            <div style={{ fontSize: 26, fontWeight: 900 }}>Supported Live Shows</div>
            <div style={{ margin: "16px 0 12px", textAlign: "left", display: "grid", gap: 10 }}>
              {SUPPORTED_SHOWS.map((name) => (
                <div key={name} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 19, fontWeight: 700 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                  {name}
                </div>
              ))}
            </div>
            <p style={{ fontSize: 16, color: C.muted, lineHeight: 1.4, margin: "0 0 20px" }}>{SHOWS_NOTE}</p>
            <PrimaryBtn onClick={() => setShowShows(false)} style={{ width: "100%", flex: "none", ...ring("gotit") }}>Got it</PrimaryBtn>
          </Sheet>
        )}

        {step === "done" && (
          <Sheet>
            <div style={{ fontSize: 30, fontWeight: 900 }}>Order placed!</div>
            <p style={{ fontSize: 18, color: C.sub, lineHeight: 1.45, margin: "10px 0 24px" }}>
            {resultMsg || `Thank you! Your MagicBeacon order is confirmed. ${offer?.ship_text || SHIP_TEXT}. We'll text you a confirmation now and let you know as soon as it ships.`}            </p>
            <PrimaryBtn onClick={onBack} style={{ width: "100%", flex: "none", ...ring("done") }}>Done</PrimaryBtn>
          </Sheet>
        )}

        {step === "failed" && (
          <Sheet>
            <div style={{ fontSize: 28, fontWeight: 900 }}>Order not completed</div>
            <p style={{ fontSize: 18, color: C.red, lineHeight: 1.45, margin: "10px 0 24px" }}>{resultMsg}</p>
            <div style={{ display: "flex", gap: 12 }}>
              <GrayBtn onClick={onBack} style={ring("fclose")}>Close</GrayBtn>
              <PrimaryBtn onClick={() => { setDigits(""); setStepError(""); setStep("phone"); }} style={ring("retry")}>Try Again</PrimaryBtn>
            </div>
          </Sheet>
        )}
      </div>
    </div>
  );
}
